import { Action, Selector, State, StateContext } from "@ngxs/store";
import { Administration, Administrations, AssignInterviewers, AssignModerators, CreateAdministration, DeleteAdministration, GetSingleAdminstration, SetFilteredAdministrations, SetSearchQuery, UpdateAdministration } from "@tsin-core/states/administration/administration.action";
import { AdministrationRequestModel, AdministrationStateModel, IAdministrationResponseModel } from "@tsin-core/states/administration/administration.model";
import { AdministrationService } from "@tsin-core/services/http/administration.service";
import { catchError, tap } from "rxjs";
import { LoadingState } from "@tsin-core/entities";
import { inject, Injectable } from "@angular/core";

@State<AdministrationStateModel>({
  name: 'administrationState',
  defaults: {
    searchQuery: '',
    filteredAdministrations: '',
    administrations: [],
    administration: null,
    adminstrationAssignment: null,
    loading: LoadingState.notLoading
  }
})

@Injectable()

export class AdministrationState {

  private administrationService = inject(AdministrationService)

  @Selector()
  static getAdministrations(state: AdministrationStateModel) : IAdministrationResponseModel[] {
    return state.administrations;
  }

  @Selector()
  static getAdministration(state: AdministrationStateModel) {
    return state.administration;
  }

  @Selector()
  static getLoading(state: AdministrationStateModel) {
    return state.loading;
  }

  @Selector()
  static filteredItems(state: AdministrationStateModel) {
    const query = state.searchQuery;
    if (!query || query === undefined) {
      return state.administrations;  // Return all administrations when searchQuery is empty
    }
    return state.administrations.filter((administration: any) =>
      administration?.cohort?.id?.includes(query.toLowerCase()) ||
      administration?.dayTime?.toLowerCase().includes(query.toLowerCase()) ||
      administration?.day?.toLowerCase().includes(query.toLowerCase())  // Include partial matches
    );
  }

  @Selector()
  static filteredAdministrations(state: AdministrationStateModel) {
    const filter = state.filteredAdministrations;
    if (!filter || filter === undefined) {
      return [];  // Return all administrations when searchQuery is empty
    }
    return state.administrations.filter(
      (administration) => administration.cohort.id === filter
    );
  }

  @Action(Administrations)
  getAdministrations({ patchState, getState, setState }: StateContext<AdministrationStateModel>) {
    patchState({ loading: LoadingState.loadingList });
    return this.administrationService.getAdministrations().pipe(
      tap((administrations: IAdministrationResponseModel[]) => {
        // const state = getState();
        // setState({
        // ...state,
        // administrations: administrations,
        // });
        patchState({ administrations: administrations, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(Administration)
  getAdministration(ctx: StateContext<AdministrationStateModel>, action: Administration) {
    return this.administrationService.getAdministration(action.payload).pipe(
      tap((administration: AdministrationRequestModel) => {
        const state = ctx.getState();
        ctx.setState({
          ...state,
          administration: administration,
        });
      }),
      catchError((error) => {
        console.error('Failed to load administrations', error);
        throw error
      })
    );
  }

  @Action(GetSingleAdminstration)
  getSingleAdministration(ctx: StateContext<AdministrationStateModel>, action: Administration) {
    return this.administrationService.getSingleAdministration(action.payload).pipe(
      tap((administration: AdministrationRequestModel) => {
        const state = ctx.getState();
        ctx.setState({
          ...state,
          administration: administration,
        });
      }),
      catchError((error) => {
        console.error('Failed to load administrations', error);
        throw error
      })
    );
  }



  @Action(CreateAdministration)
  createAdministration({ patchState, getState }: StateContext<AdministrationStateModel>, action: CreateAdministration) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.administrationService.createAdministration(action.payload).pipe(
      tap((result: IAdministrationResponseModel) => {
        const state = getState();
        patchState({
          administrations: [...state.administrations, result],
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(UpdateAdministration)
  updateAdministration({ patchState, getState }: StateContext<AdministrationStateModel>, action: UpdateAdministration) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.administrationService.updateAdministration(action.payload).pipe(
      tap((result: IAdministrationResponseModel) => {
        // // console.log('UPDATE >>', result);
        let administrations = getState().administrations;
        const index = administrations.findIndex((administration) => administration.id === result.id);
        administrations[index] = result;
        patchState({
          administrations,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error
      })
    );
  }

  @Action(DeleteAdministration)
  deleteAdministration({ patchState, getState }: StateContext<AdministrationStateModel>, action: DeleteAdministration) {
    patchState({ loading: LoadingState.loadingDelete });
    return this.administrationService.deleteAdministration(action.payload).pipe(
      tap(() => {
        let administrations = getState().administrations;
        administrations = administrations.filter(
          (administration) => administration.id !== action.payload
        );
        patchState({
          administrations,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error
      })
    );
  }


  @Action(AssignModerators)
  assignModerators(ctx: StateContext<AdministrationStateModel>, action: AssignModerators) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.administrationService.assignModerators(action.payload).pipe(
      tap((result: any) => {
        const state = ctx.getState();
        ctx.patchState({
          administration: result,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(AssignInterviewers)
  assignInterviewers(ctx: StateContext<AdministrationStateModel>, action: AssignInterviewers) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.administrationService.assignInterviewers(action.payload).pipe(
      tap((result: any) => {
        const state = ctx.getState();
        ctx.patchState({
          administration: result,
          loading: LoadingState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  // Action to set the search query
  @Action(SetSearchQuery)
  setSearchQuery(ctx: StateContext<AdministrationStateModel>, action: SetSearchQuery) {
    ctx.patchState({ loading: LoadingState.loadingList });
    setTimeout(() => {
      ctx.patchState({
        searchQuery: action.query,
        loading: LoadingState.notLoading
      });
    }, 1000)
  }

  @Action(SetFilteredAdministrations)
  setFilteredAdministrations(ctx: StateContext<AdministrationStateModel>, action: SetFilteredAdministrations) {
    ctx.patchState({ loading: LoadingState.loadingList });
    setTimeout(() => {
      ctx.patchState({
        filteredAdministrations: action.payload,
        loading: LoadingState.notLoading
      });
    }, 1000)
  }

}
